import { type FC, type ReactElement, useCallback } from "react"

import DragDropWrapper, { useDraggableItemRefs } from "@/shared/drag-n-drop/DragDropWrapper"
import { GoalsSortType } from "@/shared/types/sort"

import { BehavioralChange } from "../model/behavioral-goal"

import { BehavioralGoalCard } from "./BehavioralGoalCard"

type Props = {
    archivedBehavioral: number[]
    goals: BehavioralChange[]
    sort: GoalsSortType
    onBlurBehavioralChange: (index: number) => void
    onArchiveClick: (index: number) => void
    onDragEnd: (result: any) => void
    setHasArchivedPractices: () => void
    showInitialProjectedDeleteModal: (value: string, index: number) => void
    dragAreaId: string
}

export const BehavioralList: FC<Props> = ({
    archivedBehavioral,
    goals,
    sort,
    onBlurBehavioralChange,
    onArchiveClick,
    onDragEnd,
    setHasArchivedPractices,
    showInitialProjectedDeleteModal,
    dragAreaId
}: Props): ReactElement => {
    const isArchived = useCallback(
        (id: number) => {
            return archivedBehavioral?.includes(id)
        },
        [archivedBehavioral]
    )

    return (
        <DragDropWrapper onDrop={onDragEnd}>
            {goals.map((goal: BehavioralChange, index: number): ReactElement => {
                const isArchivedChange: boolean = isArchived(goal.id)

                return (
                    <CardWrapper
                        key={goal.uuid}
                        dropArea={dragAreaId}
                        index={index}
                        sort={sort}
                        onBlurBehavioralChange={onBlurBehavioralChange}
                        onArchiveClick={onArchiveClick}
                        setHasArchivedPractices={setHasArchivedPractices}
                        showInitialProjectedDeleteModal={showInitialProjectedDeleteModal}
                        isArchivedChange={isArchivedChange}
                        goal={goal}
                        goalsLength={goals.length}
                    />
                )
            })}
        </DragDropWrapper>
    )
}

function CardWrapper({
    dropArea,
    goal,
    setHasArchivedPractices,
    showInitialProjectedDeleteModal,
    goalsLength,
    onBlurBehavioralChange,
    index,
    sort,
    isArchivedChange,
    onArchiveClick
}): ReactElement {
    const { containerRef, grabberRef }: ReturnType<typeof useDraggableItemRefs> = useDraggableItemRefs({
        index,
        id: goal.uuid,
        dropType: dropArea
    })

    return (
        <BehavioralGoalCard
            setHasArchivedPractices={setHasArchivedPractices}
            showInitialProjectedDeleteModal={showInitialProjectedDeleteModal}
            showDrag={goalsLength > 1 && !isArchivedChange}
            isArchived={isArchivedChange}
            onBlurBehavioralChange={onBlurBehavioralChange}
            index={index}
            snapshot={grabberRef}
            sort={sort}
            onArchive={onArchiveClick}
            provider={containerRef}
        />
    )
}
